/* ============================================================
   Component: Checkout Address (Ara Adres Sayfası)
   ------------------------------------------------------------
   Form submit flow:
   1) Client-side validation (TCKN, VKN, phone, required fields)
   2) POST /apps/wishlist/checkout/address  (app proxy)
      → App: customerUpdate addresses + setAsDefault
      → App: metaobjectCreate (invoice info) — returns handle
   3) POST /cart/update.js with attributes (invoice data + metaobject handle)
      → These propagate to order.note_attributes automatically
   4) Redirect to /checkout (Shopify picks customer's default address)
   ============================================================ */

(function () {
  'use strict';

  var form = document.getElementById('checkout-address-form');
  if (!form) return;

  var submitBtn = form.querySelector('[data-submit]');
  var submitLabel = form.querySelector('.checkout-address__btn-label');
  var submitSpinner = form.querySelector('.checkout-address__btn-spinner');
  var statusBox = form.querySelector('[data-status]');
  var individualPanel = form.querySelector('[data-invoice-individual]');
  var corporatePanel = form.querySelector('[data-invoice-corporate]');
  var customerId = form.dataset.customerId;

  /* ---------------- Shared helpers ---------------- */
  function normalize(s) {
    return (s || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  function matchInList(value, list) {
    if (!value) return null;
    if (list.indexOf(value) !== -1) return value;
    var n = normalize(value);
    for (var i = 0; i < list.length; i++) {
      if (normalize(list[i]) === n) return list[i];
    }
    return null;
  }

  /* cascade(province, city) — set by initLocationDropdowns, used by picker */
  var cascade = null;
  /* loadMahalleler(province, district) — set by initLocationDropdowns, populates datalist */
  var loadMahalleler = null;
  /* reconcileMahalle() — set by initLocationDropdowns; listede olmayan mahalleyi temizler */
  var reconcileMahalle = null;

  /* ---------------- Address combine/parse (mahalle + bina/daire/kat) ---------------- */
  function combineAddress1(mahalle, street) {
    var m = (mahalle || '').trim();
    var s = (street || '').trim();
    if (!m) return s;
    var normalized = /\bmah(\.|allesi)?\b/i.test(m) ? m : m + ' Mah.';
    return s ? (normalized + ', ' + s) : normalized;
  }

  function combineAddress2(bina, daire, kat, extra) {
    var parts = [];
    if (bina) parts.push('Bina No: ' + String(bina).trim());
    if (daire) parts.push('Daire No: ' + String(daire).trim());
    if (kat) parts.push('Kat: ' + String(kat).trim());
    if (extra) parts.push(String(extra).trim());
    return parts.join(' / ');
  }

  function parseAddress1(raw) {
    var s = (raw || '').trim();
    if (!s) return { mahalle: '', street: '' };
    var m = s.match(/^([^,]+?)\s+Mah(?:\.|allesi)?\.?\s*,?\s*(.*)$/i);
    if (m) {
      return { mahalle: m[1].trim(), street: m[2].trim() };
    }
    return { mahalle: '', street: s };
  }

  function parseAddress2(raw) {
    var s = (raw || '').trim();
    var out = { bina: '', daire: '', kat: '', extra: '' };
    if (!s) return out;
    var matched = false;
    var bm = s.match(/Bina\s*No\s*:\s*([^\/,]+?)(?=\s*[\/,]|$)/i);
    if (bm) { out.bina = bm[1].trim(); matched = true; }
    var dm = s.match(/Daire\s*No\s*:\s*([^\/,]+?)(?=\s*[\/,]|$)/i);
    if (dm) { out.daire = dm[1].trim(); matched = true; }
    var km = s.match(/Kat\s*:\s*([^\/,]+?)(?=\s*[\/,]|$)/i);
    if (km) { out.kat = km[1].trim(); matched = true; }
    if (matched) {
      var residue = s
        .replace(/Bina\s*No\s*:\s*[^\/,]+\s*[\/,]?\s*/i, '')
        .replace(/Daire\s*No\s*:\s*[^\/,]+\s*[\/,]?\s*/i, '')
        .replace(/Kat\s*:\s*[^\/,]+\s*[\/,]?\s*/i, '')
        .replace(/^[\s\/,]+|[\s\/,]+$/g, '')
        .trim();
      out.extra = residue;
    } else {
      out.extra = s;
    }
    return out;
  }

  /* ---------------- İl/İlçe dropdown init ---------------- */
  (function initLocationDropdowns() {
    var provinceSelect = form.querySelector('#ca-province');
    var citySelect = form.querySelector('#ca-city');
    var data = window.TR_PROVINCES;
    if (!provinceSelect || !citySelect || !data) return;

    function populateCities(province) {
      citySelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      if (!province || !data[province]) {
        placeholder.textContent = 'Önce il seçin';
        citySelect.appendChild(placeholder);
        citySelect.disabled = true;
        return;
      }
      placeholder.textContent = 'Seçiniz';
      citySelect.appendChild(placeholder);
      citySelect.disabled = false;
      var cities = data[province].slice().sort(function (a, b) {
        return a.localeCompare(b, 'tr-TR');
      });
      cities.forEach(function (name) {
        var opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name;
        citySelect.appendChild(opt);
      });
    }

    var provinces = Object.keys(data).sort(function (a, b) {
      return a.localeCompare(b, 'tr-TR');
    });
    provinces.forEach(function (name) {
      var opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      provinceSelect.appendChild(opt);
    });

    var defaultProvince = provinceSelect.getAttribute('data-default') || '';
    var matchedProvince = matchInList(defaultProvince, provinces);
    if (matchedProvince) {
      provinceSelect.value = matchedProvince;
      populateCities(matchedProvince);
      var defaultCity = citySelect.getAttribute('data-default') || '';
      var matchedCity = matchInList(defaultCity, data[matchedProvince]);
      if (matchedCity) citySelect.value = matchedCity;
    }

    /* ---- Mahalle combobox attach + populate ---- */
    var mahalleCombobox = null;
    var mahalleAbort = null;
    var mahalleCache = {};

    function ensureCombobox() {
      if (mahalleCombobox) return mahalleCombobox;
      var el = form.querySelector('[data-mahalle-combobox]');
      if (el && window.MytheMahalle && window.MytheMahalle.attach) {
        mahalleCombobox = window.MytheMahalle.attach(el);
      }
      return mahalleCombobox;
    }

    function setMahalleler(items) {
      var c = ensureCombobox();
      if (c) c.setItems(items);
    }

    function extractItems(body) {
      if (!body) return [];
      if (Array.isArray(body)) return body;
      if (Array.isArray(body.items)) return body.items;
      if (Array.isArray(body.neighborhoods)) return body.neighborhoods;
      if (Array.isArray(body.data)) return body.data;
      if (Array.isArray(body.results)) return body.results;
      return [];
    }

    function fetchMahalleler(province, district) {
      ensureCombobox();
      if (!province || !district) { setMahalleler([]); return; }
      var key = province + '||' + district;
      if (mahalleCache[key]) { setMahalleler(mahalleCache[key]); return; }
      if (mahalleAbort) { try { mahalleAbort.abort(); } catch (e) {} }
      mahalleAbort = new AbortController();
      if (mahalleCombobox && mahalleCombobox.setLoading) mahalleCombobox.setLoading(true);
      var url = '/apps/wishlist/locations/neighborhoods?province='
        + encodeURIComponent(province) + '&district=' + encodeURIComponent(district);
      fetch(url, { signal: mahalleAbort.signal, headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (!r.ok) return null;
          var ct = r.headers.get('content-type') || '';
          if (ct.indexOf('application/json') === -1) return null;
          return r.json();
        })
        .then(function (body) {
          var items = extractItems(body);
          mahalleCache[key] = items;
          setMahalleler(items);
        })
        .catch(function () { setMahalleler([]); });
    }

    loadMahalleler = fetchMahalleler;
    reconcileMahalle = function () {
      var c = ensureCombobox();
      return c && c.reconcile ? c.reconcile() : true;
    };

    /* Kullanıcı il/ilçe değiştirince eski mahalle yeni listeye ait olmaz */
    provinceSelect.addEventListener('change', function () {
      populateCities(provinceSelect.value);
      provinceSelect.removeAttribute('aria-invalid');
      form.mahalle.value = '';
      setMahalleler([]);
    });
    citySelect.addEventListener('change', function () {
      citySelect.removeAttribute('aria-invalid');
      form.mahalle.value = '';
      fetchMahalleler(provinceSelect.value, citySelect.value);
    });

    /* Initial fetch if both province+city pre-filled */
    if (provinceSelect.value && citySelect.value) {
      fetchMahalleler(provinceSelect.value, citySelect.value);
    }

    cascade = function setProvinceCity(province, city) {
      var matched = matchInList(province, provinces);
      if (matched) {
        provinceSelect.value = matched;
        populateCities(matched);
        var mCity = matchInList(city, data[matched]);
        if (mCity) {
          citySelect.value = mCity;
          fetchMahalleler(matched, mCity);
        } else {
          setMahalleler([]);
        }
      } else {
        provinceSelect.value = '';
        populateCities(null);
        setMahalleler([]);
      }
    };
  })();

  /* ---------------- Address picker (saved address selector) ---------------- */
  var picker = form.querySelector('[data-address-picker]');
  var shippingWrap = form.querySelector('[data-shipping-wrap]');
  var editingId = null;

  function extractAddr(dataset) {
    return {
      firstName: dataset.firstName || '',
      lastName: dataset.lastName || '',
      phone: dataset.phone || '',
      address1: dataset.address1 || '',
      address2: dataset.address2 || '',
      city: dataset.city || '',
      province: dataset.province || '',
      zip: dataset.zip || '',
    };
  }

  function inferLegacyLocation(d) {
    var data = window.TR_PROVINCES;
    if (!data) return;
    var provinces = Object.keys(data);
    if (d.province && matchInList(d.province, provinces)) return;

    if (d.zip && !/^[0-9]+$/.test(String(d.zip).trim())) {
      for (var i = 0; i < provinces.length; i++) {
        var mDist = matchInList(d.zip, data[provinces[i]]);
        if (mDist) {
          d.province = provinces[i];
          d.city = mDist;
          d.zip = '';
          return;
        }
      }
    }

    if (d.city) {
      for (var j = 0; j < provinces.length; j++) {
        var mDist2 = matchInList(d.city, data[provinces[j]]);
        if (mDist2) {
          d.province = provinces[j];
          d.city = mDist2;
          return;
        }
      }
      var mProv = matchInList(d.city, provinces);
      if (mProv) {
        d.province = mProv;
        d.city = '';
      }
    }
  }

  function isCompleteAddress(addr) {
    if (!addr.firstName || !addr.lastName || !addr.phone) return false;
    if (!addr.address1) return false;
    if (!addr.province || !addr.city) return false;
    var data = window.TR_PROVINCES;
    if (!data) return true;
    var provinces = Object.keys(data);
    var mProv = matchInList(addr.province, provinces);
    if (!mProv) return false;
    /* İlçe: city bir ilçe mi (eski kayıt), değilse address2'den çözülebiliyor mu (app formatı) */
    if (matchInList(addr.city, data[mProv])) return true;
    var p2 = parseAddress2(addr.address2 || '');
    if (extractDistrictFromExtra(p2.extra, data[mProv]).district) return true;
    return false;
  }

  function clearShippingAria() {
    if (!shippingWrap) return;
    shippingWrap.querySelectorAll('[aria-invalid]').forEach(function (el) {
      el.removeAttribute('aria-invalid');
    });
  }

  function clearShippingForm() {
    form.first_name.value = '';
    form.last_name.value = '';
    form.phone.value = '';
    form.mahalle.value = '';
    form.address1.value = '';
    form.bina_no.value = '';
    form.daire_no.value = '';
    form.kat.value = '';
    form.address2.value = '';
    form.zip.value = '';
    if (cascade) cascade('', '');
    clearShippingAria();
  }

  /* Serbest metinden, ilçe adına (addr.city) tam olarak eşit token'ları çıkarır.
     Sunucu ilçeyi address2'nin önüne eklediği için, kayıtlı adres yeniden
     yüklenirken ilçe "Ek adres tarifi" serbest metin kutusuna sızabiliyor;
     bu da her kayıtta tekrar birikmesine yol açıyordu. İlçe zaten İl/İlçe
     dropdown'ında tutulduğundan serbest metinden güvenle temizlenir. */
  function stripDistrictFromExtra(extra, district) {
    var target = normalize(district);
    if (!target) return (extra || '').trim();
    return String(extra || '')
      .split(/\s*[,\/]\s*/)
      .map(function (t) { return t.trim(); })
      .filter(function (t) { return t && normalize(t) !== target; })
      .join(', ');
  }

  /* App formatı: İlçe address2 başına yazılır (Shopify city = İl). address2'nin
     serbest kısmından İlçe token'ını (il'in ilçe listesinde eşleşen) ayıklar;
     kalanı not olarak döner. */
  function extractDistrictFromExtra(extra, districts) {
    var tokens = String(extra || '')
      .split(/\s*[,\/]\s*/)
      .map(function (t) { return t.trim(); })
      .filter(Boolean);
    var district = '';
    var noteTokens = [];
    for (var i = 0; i < tokens.length; i++) {
      var m = (!district && districts) ? matchInList(tokens[i], districts) : null;
      if (m) { district = m; } else { noteTokens.push(tokens[i]); }
    }
    return { district: district, note: noteTokens.join(', ') };
  }

  function fillShippingForm(addr) {
    form.first_name.value = addr.firstName || '';
    form.last_name.value = addr.lastName || '';
    form.phone.value = addr.phone || '';
    var parsed1 = parseAddress1(addr.address1 || '');
    var parsed2 = parseAddress2(addr.address2 || '');
    form.mahalle.value = parsed1.mahalle;
    form.address1.value = parsed1.street;
    form.bina_no.value = parsed2.bina;
    form.daire_no.value = parsed2.daire;
    form.kat.value = parsed2.kat;

    /* İlçe'yi çöz. App formatında Shopify city = İl'dir ve İlçe address2 başındadır.
       - addr.city geçerli bir ilçe adıysa (eski kayıt) → onu kullan
       - değilse (app formatı) → İlçe'yi address2'den ayıkla, notu ayır */
    var data = window.TR_PROVINCES;
    var provincesList = data ? Object.keys(data) : [];
    var ilKey = matchInList(addr.province, provincesList) || '';
    var districtsList = (ilKey && data) ? data[ilKey] : null;
    var ilce = '';
    var noteExtra = parsed2.extra;
    if (districtsList && matchInList(addr.city, districtsList)) {
      ilce = matchInList(addr.city, districtsList);
    } else {
      var extracted = extractDistrictFromExtra(parsed2.extra, districtsList);
      ilce = extracted.district;
      noteExtra = extracted.note;
    }

    /* Serbest metne sızan ilçeyi temizle (eski kayıt birikimi için de güvenli) */
    form.address2.value = stripDistrictFromExtra(noteExtra, ilce);
    form.zip.value = addr.zip || '';
    if (cascade) cascade(addr.province || '', ilce || '');
    clearShippingAria();
  }

  function getSelectedOption() {
    var radio = form.elements['ca-selected'];
    if (!radio) return null;
    if (radio.length !== undefined) {
      for (var i = 0; i < radio.length; i++) {
        if (radio[i].checked) return radio[i].value;
      }
      return null;
    }
    return radio.checked ? radio.value : null;
  }

  function refreshShippingVisibility() {
    if (!picker || !shippingWrap) return;
    var selected = getSelectedOption();
    if (selected === null) { shippingWrap.hidden = true; return; }
    if (selected === 'new') { shippingWrap.hidden = false; return; }
    shippingWrap.hidden = (editingId !== selected);
  }

  function handleOptionChange(value) {
    clearStatus();
    editingId = null;
    if (value === 'new') {
      clearShippingForm();
    } else {
      var card = picker.querySelector('[data-option-id="' + value + '"]');
      if (card) {
        var addr = extractAddr(card.dataset);
        inferLegacyLocation(addr);
        fillShippingForm(addr);
        if (!isCompleteAddress(addr)) editingId = value;
      }
    }
    refreshShippingVisibility();
  }

  if (picker) {
    // Flag incomplete cards visually
    picker.querySelectorAll('[data-option-id]').forEach(function (card) {
      var addr = extractAddr(card.dataset);
      inferLegacyLocation(addr);
      if (!isCompleteAddress(addr)) {
        card.classList.add('checkout-address__option--incomplete');
      }
    });

    picker.addEventListener('change', function (e) {
      if (e.target.name !== 'ca-selected') return;
      handleOptionChange(e.target.value);
    });

    picker.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-edit-option]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      clearStatus();
      var id = btn.getAttribute('data-edit-option');
      var radio = form.querySelector('input[name="ca-selected"][value="' + id + '"]');
      if (radio) radio.checked = true;
      editingId = id;
      var card = picker.querySelector('[data-option-id="' + id + '"]');
      if (card) {
        var addr = extractAddr(card.dataset);
        inferLegacyLocation(addr);
        fillShippingForm(addr);
      }
      refreshShippingVisibility();
    });

    // Initial selection: default → first saved → "new"
    var initialCard = picker.querySelector('[data-default="true"]')
      || picker.querySelector('[data-option-id]');
    var initialValue = initialCard ? initialCard.getAttribute('data-option-id') : 'new';
    var initialRadio = form.querySelector('input[name="ca-selected"][value="' + initialValue + '"]');
    if (initialRadio) initialRadio.checked = true;
    handleOptionChange(initialValue);
  }

  /* ---------------- Invoice type toggle ---------------- */
  form.querySelectorAll('input[name="invoice_type"]').forEach(function (radio) {
    radio.addEventListener('change', function () {
      var isCorporate = radio.value === 'corporate' && radio.checked;
      if (isCorporate) {
        individualPanel.hidden = true;
        corporatePanel.hidden = false;
      } else {
        individualPanel.hidden = false;
        corporatePanel.hidden = true;
      }
      clearStatus();
    });
  });

  /* ---------------- Validators ---------------- */

  function isValidTCKN(v) {
    if (!v || !/^[1-9][0-9]{10}$/.test(v)) return false;
    var d = v.split('').map(Number);
    var oddSum = d[0] + d[2] + d[4] + d[6] + d[8];
    var evenSum = d[1] + d[3] + d[5] + d[7];
    var digit10 = ((oddSum * 7) - evenSum) % 10;
    if (digit10 < 0) digit10 += 10;
    if (digit10 !== d[9]) return false;
    var sumFirst10 = 0;
    for (var i = 0; i < 10; i++) sumFirst10 += d[i];
    return (sumFirst10 % 10) === d[10];
  }

  function isValidVKN(v) {
    return /^[0-9]{10}$/.test(v);
  }

  var ERROR_MESSAGES = {
    invalid_tckn: 'Girdiğiniz TC Kimlik No geçerli değil.',
    invalid_vkn: 'Girdiğiniz Vergi Kimlik No geçerli değil.',
    invalid_phone: 'Girdiğiniz telefon numarası geçerli değil.',
    invalid_customer: 'Hesap doğrulanamadı, lütfen çıkış yapıp tekrar giriş yapın.',
    missing_customer: 'Hesap doğrulanamadı, lütfen çıkış yapıp tekrar giriş yapın.',
    missing_address: 'Teslimat adresi bilgileri eksik.',
    missing_fields: 'Zorunlu alanlar eksik, lütfen kontrol edin.',
    address_create_failed: 'Adres kaydedilemedi, lütfen bilgileri kontrol edin.',
    metaobject_create_failed: 'Fatura bilgileri kaydedilemedi, lütfen tekrar deneyin.',
    unauthorized: 'Oturumunuz sona ermiş olabilir, lütfen tekrar giriş yapın.',
  };

  function friendlyError(code) {
    if (!code) return 'Bilgileriniz kaydedilemedi, lütfen tekrar deneyin.';
    return ERROR_MESSAGES[code] || 'Bilgileriniz kaydedilemedi, lütfen tekrar deneyin.';
  }

  function normalizePhone(raw) {
    if (!raw) return '';
    var digits = raw.replace(/\D/g, '');
    if (digits.startsWith('90')) digits = digits.slice(2);
    if (digits.startsWith('0')) digits = digits.slice(1);
    if (digits.length !== 10) return '';
    return '+90' + digits;
  }

  function fieldError(input, msg) {
    if (msg) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
  }

  function clearStatus() {
    if (!statusBox) return;
    statusBox.hidden = true;
    statusBox.textContent = '';
    statusBox.className = 'checkout-address__status';
  }

  function showStatus(msg, kind) {
    if (!statusBox) return;
    statusBox.textContent = msg;
    statusBox.hidden = false;
    statusBox.className = 'checkout-address__status checkout-address__status--' + (kind || 'error');
    statusBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function setBusy(busy) {
    submitBtn.disabled = busy;
    if (submitSpinner) submitSpinner.hidden = !busy;
    if (submitLabel) submitLabel.textContent = busy ? 'Kaydediliyor...' : 'Ödemeye Geç';
  }

  /* ---------------- Submit ---------------- */

  /* --- Kargo onay kutusu --- */
  var ackBox = form.querySelector('#ca-shipping-ack');
  var ackError = form.querySelector('[data-ack-error]');
  if (ackBox) {
    ackBox.addEventListener('change', function () {
      if (ackBox.checked && ackError) ackError.hidden = true;
    });
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    clearStatus();

    /* Mahalle listeden seçilmemişse temizlenir → aşağıdaki zorunlu alan kontrolüne takılır */
    if (reconcileMahalle) reconcileMahalle();

    var data = {
      first_name: form.first_name.value.trim(),
      last_name: form.last_name.value.trim(),
      phone: form.phone.value.trim(),
      province: form.province.value.trim(),
      city: form.city.value.trim(),
      mahalle: form.mahalle.value.trim(),
      address1: form.address1.value.trim(),
      bina_no: form.bina_no.value.trim(),
      daire_no: form.daire_no.value.trim(),
      kat: form.kat.value.trim(),
      address2: form.address2.value.trim(),
      zip: form.zip.value.trim(),
      country: 'Türkiye',
      note: form.note.value.trim(),
      invoice_type: form.invoice_type.value,
      tckn: (form.tckn && form.tckn.value || '').trim(),
      company: (form.company && form.company.value || '').trim(),
      vkn: (form.vkn && form.vkn.value || '').trim(),
      tax_office: (form.tax_office && form.tax_office.value || '').trim(),
      efatura_payer: form.efatura_payer ? !!form.efatura_payer.checked : false,
      shipping_ack: form.shipping_ack ? !!form.shipping_ack.checked : false,
    };

    /* --- Required checks --- */
    var requiredMap = {
      first_name: form.first_name,
      last_name: form.last_name,
      phone: form.phone,
      province: form.province,
      city: form.city,
      mahalle: form.mahalle,
      address1: form.address1,
      bina_no: form.bina_no,
      daire_no: form.daire_no,
    };

    var firstInvalid = null;
    Object.keys(requiredMap).forEach(function (key) {
      var el = requiredMap[key];
      if (!data[key]) {
        fieldError(el, 'required');
        if (!firstInvalid) firstInvalid = el;
      } else {
        fieldError(el, null);
      }
    });

    /* --- Phone normalize --- */
    var normalizedPhone = normalizePhone(data.phone);
    if (!normalizedPhone) {
      fieldError(form.phone, 'invalid');
      if (!firstInvalid) firstInvalid = form.phone;
    }

    /* --- ZIP 5-digit --- */
    if (data.zip && !/^[0-9]{5}$/.test(data.zip)) {
      fieldError(form.zip, 'invalid');
      if (!firstInvalid) firstInvalid = form.zip;
    }

    /* --- Invoice-specific --- */
    if (data.invoice_type === 'individual') {
      if (data.tckn && !isValidTCKN(data.tckn)) {
        fieldError(form.tckn, 'invalid');
        if (!firstInvalid) firstInvalid = form.tckn;
      } else {
        fieldError(form.tckn, null);
      }
    } else if (data.invoice_type === 'corporate') {
      if (!data.company) {
        fieldError(form.company, 'required');
        if (!firstInvalid) firstInvalid = form.company;
      } else {
        fieldError(form.company, null);
      }
      if (!isValidVKN(data.vkn)) {
        fieldError(form.vkn, 'invalid');
        if (!firstInvalid) firstInvalid = form.vkn;
      } else {
        fieldError(form.vkn, null);
      }
      if (!data.tax_office) {
        fieldError(form.tax_office, 'required');
        if (!firstInvalid) firstInvalid = form.tax_office;
      } else {
        fieldError(form.tax_office, null);
      }
    }

    /* --- Kargo onay kutusu (zorunluysa) --- */
    if (ackBox && ackBox.getAttribute('data-required') === 'true' && !ackBox.checked) {
      if (ackError) ackError.hidden = false;
      ackBox.setAttribute('aria-invalid', 'true');
      if (!firstInvalid) firstInvalid = ackBox;
    } else {
      if (ackError) ackError.hidden = true;
      if (ackBox) ackBox.removeAttribute('aria-invalid');
    }

    if (firstInvalid) {
      // If validation failed while in summary mode, auto-switch to editing so
      // user can see the form and fix the issue.
      if (picker) {
        var currentSel = getSelectedOption();
        if (currentSel && currentSel !== 'new' && editingId !== currentSel) {
          editingId = currentSel;
          refreshShippingVisibility();
        }
      }
      showStatus('Lütfen işaretli alanları kontrol edin.', 'error');
      firstInvalid.focus();
      return;
    }

    submit(data, normalizedPhone);
  });

  function submit(data, normalizedPhone) {
    setBusy(true);

    var selectedOpt = getSelectedOption();
    var existingAddressId = (selectedOpt && selectedOpt !== 'new') ? selectedOpt : null;

    var combinedAddress1 = combineAddress1(data.mahalle, data.address1);
    var combinedAddress2 = combineAddress2(data.bina_no, data.daire_no, data.kat, data.address2);

    var payload = {
      customerId: customerId,
      existingAddressId: existingAddressId,
      shipping: {
        firstName: data.first_name,
        lastName: data.last_name,
        phone: normalizedPhone,
        address1: combinedAddress1,
        address2: combinedAddress2,
        city: data.city,
        province: data.province,
        zip: data.zip,
        country: 'Turkey',
      },
      invoice: {
        type: data.invoice_type,
        tckn: data.invoice_type === 'individual' ? data.tckn : null,
        company: data.invoice_type === 'corporate' ? data.company : null,
        vkn: data.invoice_type === 'corporate' ? data.vkn : null,
        taxOffice: data.invoice_type === 'corporate' ? data.tax_office : null,
        efaturaPayer: data.invoice_type === 'corporate' ? data.efatura_payer : false,
      },
      note: data.note,
    };

    fetch('/apps/wishlist/checkout/address', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, status: res.status, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok || !result.body || result.body.ok === false) {
          var code = (result.body && (result.body.error || result.body.reason)) || '';
          throw new Error(friendlyError(code));
        }
        return writeCartAttributes(data, normalizedPhone, result.body.metaobjectHandle);
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        setBusy(false);
        showStatus(err.message || 'Bilgileriniz kaydedilemedi, lütfen tekrar deneyin.', 'error');
      });
  }

  function writeCartAttributes(data, normalizedPhone, metaobjectHandle) {
    var attrs = {
      'Teslimat Alıcı': data.first_name + ' ' + data.last_name,
      'Teslimat Telefon': normalizedPhone,
      'Teslimat Adres': [data.address1, data.address2, data.city, data.province, data.zip]
        .filter(Boolean)
        .join(', '),
      'Fatura Türü': data.invoice_type === 'corporate' ? 'Kurumsal' : 'Bireysel',
    };

    if (data.invoice_type === 'individual') {
      if (data.tckn) attrs['TC Kimlik No'] = data.tckn;
    } else if (data.invoice_type === 'corporate') {
      attrs['Firma Adı'] = data.company;
      attrs['VKN'] = data.vkn;
      attrs['Vergi Dairesi'] = data.tax_office;
      attrs['E-Fatura Mükellefi'] = data.efatura_payer ? 'Evet' : 'Hayır';
    }

    if (data.note) {
      attrs['Teslim Notu'] = data.note;
    }

    if (data.shipping_ack) {
      attrs['Kargo Bilgilendirmesi'] = 'Onaylandı';
    }

    if (metaobjectHandle) {
      attrs['__checkout_info_handle'] = metaobjectHandle;
    }

    var body = { attributes: attrs };
    if (data.note) {
      body.note = data.note;
    }

    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error('Sepet güncellenemedi. Lütfen tekrar deneyin.');
      return res.json();
    });
  }

  /* --- Clear aria-invalid on input/change --- */
  form.querySelectorAll('input, textarea, select').forEach(function (el) {
    var evt = el.tagName === 'SELECT' ? 'change' : 'input';
    el.addEventListener(evt, function () {
      if (el.hasAttribute('aria-invalid')) {
        el.removeAttribute('aria-invalid');
      }
    });
  });
})();

/* ============================================================
   Kargo Bilgilendirme Popup
   ------------------------------------------------------------
   Sayfa açılınca section ayarlarındaki bilgilendirme popup'ını
   gösterir. Tarayıcı oturumu başına 1 kez (sessionStorage).
   ============================================================ */

(function () {
  'use strict';

  var notice = document.querySelector('[data-checkout-notice]');
  if (!notice) return;

  var storageKey = notice.dataset.storageKey || 'mf_checkout_notice_seen';

  try {
    if (sessionStorage.getItem(storageKey) === '1') return;
  } catch (_) {
    // private mode / storage blocked → yine de göster
  }

  var previousFocus = document.activeElement;

  function open() {
    notice.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      notice.classList.add('checkout-notice--open');
      var okBtn = notice.querySelector('.checkout-notice__ok');
      if (okBtn && okBtn.focus) okBtn.focus();
    });
    document.addEventListener('keydown', onKey);
  }

  function close() {
    notice.classList.remove('checkout-notice--open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    try { sessionStorage.setItem(storageKey, '1'); } catch (_) {}
    setTimeout(function () { notice.hidden = true; }, 220);
    if (previousFocus && previousFocus.focus) {
      try { previousFocus.focus(); } catch (_) {}
    }
  }

  function onKey(e) {
    if (e.key === 'Escape') close();
  }

  notice.addEventListener('click', function (e) {
    if (e.target.closest('[data-notice-close]')) close();
  });

  open();
})();
